import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoPersonAdd,
  IoCheckmarkCircle,
  IoTime,
  IoMailOutline,
} from 'react-icons/io5';
import { signOut } from 'firebase/auth';
import { auth } from '../../../firebase/config';

const PROVIDER_LABEL = {
  kakao: '카카오 계정',
  google: '구글 계정',
  apple: '애플 계정',
};

const ROLE_LABEL = {
  partner: '제휴업체',
  realtor: '공인중개사',
};

const SignupCompletePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { role, provider, name } = state;

  useEffect(() => {
    if (!role || !provider) {
      navigate('/login', { replace: true });
    }
  }, [role, provider, navigate]);

  if (!role || !provider) return null;

  const isPartner = role === 'partner';
  const roleLabel = ROLE_LABEL[role] || '회원';
  const providerLabel = PROVIDER_LABEL[provider] || '소셜 계정';

  const handleReturn = async () => {
    // 제휴업체는 가입 시 이미 signOut — 재호출해도 안전
    try { await signOut(auth); } catch {}
    navigate('/login', { replace: true });
  };

  return (
    <Wrapper>
      <Card>
        <IconBlock>
          <AvatarCircle>
            <IoPersonAdd size={24} color="#4A6CF7" />
          </AvatarCircle>
          <CheckDot>
            <IoCheckmarkCircle size={22} color="#22C55E" />
          </CheckDot>
        </IconBlock>

        <Title>
          {isPartner ? '가입 신청 완료!' : '가입 완료!'}
        </Title>
        <SubTitle>
          <strong>{roleLabel}</strong> 회원가입
          {isPartner ? ' 신청이 완료되었습니다.' : '이 완료되었습니다.'}
          <br />
          {isPartner
            ? '관리자 승인 후 서비스를 이용하실 수 있습니다.'
            : '바로 로그인하여 서비스를 시작해 보세요.'}
        </SubTitle>

        <InfoCard>
          <InfoTitle>가입 정보</InfoTitle>
          <InfoRow>
            <span>회원 유형</span>
            <strong>{roleLabel}</strong>
          </InfoRow>
          {name && (
            <InfoRow>
              <span>이름</span>
              <strong>{name}</strong>
            </InfoRow>
          )}
          <InfoRow>
            <span>소셜 계정</span>
            <strong>{providerLabel}</strong>
          </InfoRow>
          <InfoRow>
            <span>승인 상태</span>
            {isPartner ? (
              <StatusPending>
                <IoTime size={14} />
                <span>검토 중</span>
              </StatusPending>
            ) : (
              <StatusActive>
                <IoCheckmarkCircle size={14} />
                <span>활성</span>
              </StatusActive>
            )}
          </InfoRow>
        </InfoCard>

        {isPartner && (
          <NoticeBox>
            <NoticeHeader>
              <IoMailOutline size={16} color="#4A6CF7" />
              <span>다음 단계 안내</span>
            </NoticeHeader>
            <NoticeList>
              <li><em>1.</em> 담당자가 제출하신 정보를 검토합니다.</li>
              <li><em>2.</em> 승인 완료 시 등록하신 연락처로 안내드립니다.</li>
              <li><em>3.</em> 승인 후 이음 파트너스 어드민에 접속하실 수 있습니다.</li>
            </NoticeList>
          </NoticeBox>
        )}

        <PrimaryBtn onClick={handleReturn}>
          로그인 화면으로 돌아가기
        </PrimaryBtn>
      </Card>
    </Wrapper>
  );
};

export default SignupCompletePage;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bg};

  @media (min-width: 1024px) {
    align-items: center;
    background: #2f3542;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.xl};
  text-align: center;

  @media (min-width: 1024px) {
    padding: 48px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  }
`;

const IconBlock = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
`;

const AvatarCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #EFF4FF;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckDot = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const SubTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  line-height: 1.5;

  strong { color: ${({ theme }) => theme.colors.text}; font-weight: ${({ theme }) => theme.fontWeight.semibold}; }
`;

const InfoCard = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  background: #F9FAFB;
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.base};
  text-align: left;
`;

const InfoTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;

  > span {
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  > strong {
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: ${({ theme }) => theme.colors.text};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
  }
`;

const StatusPending = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.warning};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const StatusActive = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.success};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const NoticeBox = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  background: #EFF4FF;
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: left;
`;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const NoticeList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: ${({ theme }) => theme.fontSize.sm};
    color: #4A6CF7;
    line-height: 1.6;
  }
  em {
    font-style: normal;
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    min-width: 14px;
  }
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;

  &:hover { opacity: 0.9; }
`;
